import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { currentWibDate } from "@/components/billing/nbUtil";

const rp = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const EMPTY = { deskripsi: "", qty: "1", harga_satuan: "" };

const CustomerPicker = ({ value, onPick }) => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    const t = setTimeout(() => api.get("/billing/customers", { params: { q: q.trim(), limit: 8 } })
      .then(({ data }) => setRows(data.items || [])).catch(() => setRows([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  if (value) return (
    <div className="rounded-md border bg-[#F9FAFB] p-2 text-sm" data-testid="nb-customer-selected">
      <div className="flex items-start justify-between gap-2">
        <div><b>{value.nama_customer}</b> <span className="font-mono text-xs text-[#6B7280]">{value.customer_id}</span>
          <div className="text-xs text-[#4B5563]">{value.alamat || "Alamat belum diisi"}{value.no_hp ? ` · ${value.no_hp}` : ""}</div></div>
        <Button type="button" size="sm" variant="outline" onClick={() => onPick(null)} data-testid="nb-customer-change">Ganti</Button>
      </div>
    </div>
  );
  return (
    <div className="relative">
      <Input placeholder="Cari nama atau ID pelanggan (min. 2 huruf)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="nb-customer-search" />
      {rows.length > 0 && (
        <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg" data-testid="nb-customer-results">
          {rows.map((c) => (
            <button type="button" key={c.customer_id} onClick={() => { onPick(c); setQ(""); }} data-testid={`nb-customer-opt-${c.customer_id}`}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-[#F3F4F6]">
              <b>{c.nama_customer}</b> <span className="font-mono text-xs text-[#6B7280]">{c.customer_id}</span>
              <div className="truncate text-xs text-[#6B7280]">{c.alamat || "—"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const NbInvoiceForm = ({ initial, onSaved, onCancel }) => {
  const [cust, setCust] = useState(initial ? { customer_id: initial.customer_id, nama_customer: initial.customer_name, alamat: initial.customer_alamat, no_hp: initial.customer_no_hp } : null);
  const [tgl, setTgl] = useState(initial?.tanggal_invoice || currentWibDate());
  const [due, setDue] = useState(initial?.jatuh_tempo || "");
  const [items, setItems] = useState(initial ? initial.items.map((i) => ({ deskripsi: i.deskripsi, qty: String(i.qty), harga_satuan: String(i.harga_satuan) })) : [{ ...EMPTY }]);
  const [diskon, setDiskon] = useState(initial ? String(initial.diskon || "") : "");
  const [catatan, setCatatan] = useState(initial?.catatan || "");
  const [busy, setBusy] = useState(false);
  const calc = useMemo(() => {
    const sub = items.reduce((a, i) => a + Math.round((Number(i.qty) || 0) * (Number(i.harga_satuan) || 0)), 0);
    return { sub, total: sub - Math.round(Number(diskon) || 0) };
  }, [items, diskon]);
  const setItem = (k, f, v) => setItems((xs) => xs.map((x, i) => (i === k ? { ...x, [f]: v } : x)));
  const invalid = !cust || items.some((i) => !i.deskripsi.trim() || !(Number(i.qty) > 0) || !(Number(i.harga_satuan) >= 0) || i.harga_satuan === "")
    || Number(diskon) < 0 || calc.total <= 0;

  const save = async () => {
    setBusy(true);
    const body = { customer_id: cust.customer_id, tanggal_invoice: tgl, jatuh_tempo: due || null, diskon: Math.round(Number(diskon) || 0), catatan,
      items: items.map((i) => ({ deskripsi: i.deskripsi.trim(), qty: Number(i.qty), harga_satuan: Math.round(Number(i.harga_satuan)) })) };
    try {
      const { data } = initial ? await api.put(`/billing/nb-invoices/${initial.id}`, body) : await api.post("/billing/nb-invoices", body);
      toast.success(`Invoice ${data.nomor} ${initial ? "diperbarui" : "dibuat"}`);
      onSaved(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal menyimpan invoice"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="nb-form">
      <div className="text-xs text-[#6B7280]">{initial ? <>Ubah <b className="font-mono">{initial.nomor}</b></> : "Nomor invoice (INV-NB-…) dibuat otomatis oleh sistem saat disimpan."}</div>
      <div className="space-y-1"><Label>Pelanggan</Label><CustomerPicker value={cust} onPick={setCust} /></div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label htmlFor="nb-tgl">Tanggal Invoice</Label><Input id="nb-tgl" type="date" value={tgl} onChange={(e) => setTgl(e.target.value)} data-testid="nb-date" /></div>
        <div className="space-y-1"><Label htmlFor="nb-due">Jatuh Tempo (opsional)</Label><Input id="nb-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="nb-due" /></div>
      </div>
      <div className="space-y-2">
        <Label>Rincian Barang / Jasa</Label>
        {items.map((it, k) => (
          <div key={k} className="grid grid-cols-12 items-center gap-2" data-testid={`nb-item-${k}`}>
            <Input className="col-span-12 sm:col-span-5" placeholder="Nama barang / jasa" value={it.deskripsi} onChange={(e) => setItem(k, "deskripsi", e.target.value)} data-testid={`nb-item-desc-${k}`} />
            <Input className="col-span-3 sm:col-span-2" type="number" min="0" step="any" placeholder="Qty" value={it.qty} onChange={(e) => setItem(k, "qty", e.target.value)} data-testid={`nb-item-qty-${k}`} />
            <Input className="col-span-4 sm:col-span-2" type="number" min="0" step="1" placeholder="Harga" value={it.harga_satuan} onChange={(e) => setItem(k, "harga_satuan", e.target.value)} data-testid={`nb-item-price-${k}`} />
            <div className="col-span-4 text-right font-mono text-sm sm:col-span-2" data-testid={`nb-item-subtotal-${k}`}>{rp(Math.round((Number(it.qty) || 0) * (Number(it.harga_satuan) || 0)))}</div>
            <Button type="button" size="icon" variant="ghost" className="col-span-1" disabled={items.length === 1} onClick={() => setItems((xs) => xs.filter((_, i) => i !== k))} aria-label="Hapus item" data-testid={`nb-item-remove-${k}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={() => setItems((xs) => [...xs, { ...EMPTY }])} disabled={items.length >= 50} data-testid="nb-item-add"><Plus className="mr-1 h-4 w-4" />Tambah Item</Button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label htmlFor="nb-disc">Diskon (Rp, opsional)</Label><Input id="nb-disc" type="number" min="0" step="1" value={diskon} onChange={(e) => setDiskon(e.target.value)} data-testid="nb-discount" /></div>
        <div className="rounded-md border bg-[#ECFDF5] p-3 text-sm" data-testid="nb-totals">
          <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{rp(calc.sub)}</span></div>
          <div className="flex justify-between"><span>Diskon</span><span className="font-mono">-{rp(Math.round(Number(diskon) || 0))}</span></div>
          <div className="flex justify-between font-bold text-[#065F46]"><span>Total</span><span className="font-mono" data-testid="nb-total">{rp(calc.total)}</span></div>
        </div>
      </div>
      <div className="space-y-1"><Label htmlFor="nb-note">Catatan (opsional)</Label><Textarea id="nb-note" rows={2} value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="nb-note" /></div>
      {calc.total <= 0 && cust && <div className="text-xs text-[#B91C1C]" data-testid="nb-form-error">Total harus lebih dari 0 dan diskon tidak boleh melebihi subtotal.</div>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="nb-form-cancel">Batal</Button>
        <Button onClick={save} disabled={busy || invalid} className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="nb-form-save">
          {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Simpan Invoice</Button>
      </div>
    </div>
  );
};
